import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MFChannel } from '../../../Components/SDK';
import { setInvitations, setMultipleChannelsInfo, setUserInfoArr } from '../../../slice';
import logger from '../../../Helpers/logger';

const handleInvitationsResponse = (dispatch, response, createdAt = 0) => {
    const activity = response?.data?.activity || [];

    if (!Array.isArray(activity)) return;

    const channelList = [];
    const userList = [];

    activity.forEach(item => {
        const { channelDetails, initiatedBy } = item;

        if (channelDetails?.channelId) {
            channelList.push(channelDetails);
        }

        if (initiatedBy?.userId || initiatedBy?.email) {
            userList.push({
                ...initiatedBy,
                userId: initiatedBy.userId || initiatedBy.email,
            });
        }
    });

    if (channelList.length) dispatch(setMultipleChannelsInfo(channelList));
    if (userList.length) dispatch(setUserInfoArr(userList));
    dispatch(setInvitations({ activity, createdAt }));
};

export const useInvitationList = ({ initialSearch = '', pageSize = 20, debounceMs = 300 } = {}) => {
    const dispatch = useDispatch();
    const invitations = useSelector(state => state.invitations.activity);
    const [loading, setLoading] = useState(true);
    const [searching, setSearching] = useState(false);
    const [error, setError] = useState(null);
    const [searchText, setSearchText] = useState(initialSearch);
    const [hasMore, setHasMore] = useState(true);

    const debounceTimer = useRef(null);
    const lastCreatedAtRef = useRef(0);

    const fetchInvitations = useCallback(
        async ({ createdAt = 0, search = '', isSearch = false }) => {
            if (isSearch) setSearching(true);
            else setLoading(true);

            try {
                setError(null);

                const response = await MFChannel.fetchChannelInvitationList({
                    createdAt,
                    size: pageSize,
                    search,
                });

                const activity = response?.data?.activity || [];

                handleInvitationsResponse(dispatch, response, createdAt);

                setHasMore(activity.length >= pageSize);

                if (activity.length > 0) {
                    lastCreatedAtRef.current = activity[activity.length - 1].createdAt;
                }
            } catch (err) {
                logger.error('Failed to fetch invitations:', err);
                setError(err);
                setHasMore(false);
            } finally {
                isSearch && setSearching(false);
                setLoading(false);
            }
        },
        [pageSize, dispatch],
    );

    useEffect(() => {
        fetchInvitations({ createdAt: 0, search: initialSearch });
        setSearchText(initialSearch);
    }, [initialSearch, fetchInvitations]);

    const updateSearchText = useCallback(
        text => {
            clearTimeout(debounceTimer.current);
            debounceTimer.current = setTimeout(() => {
                setLoading(true);
                setHasMore(true);
                setSearchText(text);
                lastCreatedAtRef.current = 0;
                fetchInvitations({ createdAt: 0, search: text, isSearch: text.trim().length > 0 });
            }, debounceMs);
        },
        [debounceMs, fetchInvitations],
    );

    const loadNextPage = useCallback(() => {
        if (hasMore && !loading && invitations.length > 0) {
            const createdAt = lastCreatedAtRef.current || invitations[invitations.length - 1]?.createdAt || 0;
            fetchInvitations({ createdAt, search: searchText });
        }
    }, [hasMore, loading, invitations, searchText, fetchInvitations]);

    const refresh = useCallback(() => {
        lastCreatedAtRef.current = 0;
        setHasMore(true);
        fetchInvitations({ createdAt: 0, search: searchText });
    }, [searchText, fetchInvitations]);

    return {
        invitations,
        loading,
        searching,
        error,
        hasMore,
        loadNextPage,
        updateSearchText,
        refresh,
        searchText,
    };
};
